import PermissionButton from "../../components/ui/PermissionButton";
import { useState } from "react";
import {
  X,
  FileSpreadsheet,
  FileText,
  FileArchive,
  Calendar,
  Building2,
  Download,
} from "lucide-react";

export default function ExportTrialBalanceModal({
  onClose,
}) {
  const [format, setFormat] = useState("PDF");

  const [form, setForm] = useState({
    from: "2026-04-01",
    to: "2026-04-30",
    branch: "All Branches",
    includeZero: false,
  });

  const update = (field, value) =>
    setForm((p) => ({
      ...p,
      [field]: value,
    }));

  const exportReport = () => {
    console.log({
      format,
      ...form,
    });

    // TODO
    // Call backend export API

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">

      <div className="w-full max-w-2xl rounded-2xl bg-[#0b0b0f] border border-white/10 overflow-hidden">

        {/* Header */}

        <div className="flex justify-between items-center px-8 py-6 border-b border-white/10">

          <div>

            <h2 className="text-2xl font-serif">
              Export Trial Balance
            </h2>

            <p className="text-sm text-gray-400 mt-1">
              Generate printable financial reports
            </p>

          </div>

          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <X size={18} />
          </PermissionButton>

        </div>

        {/* Body */}

        <div className="p-8 space-y-8">

          {/* Export Format */}

          <div>

            <label className="block text-xs uppercase tracking-widest text-gray-400 mb-3">
              Export Format
            </label>

            <div className="grid grid-cols-3 gap-4">

              <FormatCard
                active={format === "PDF"}
                title="PDF"
                icon={FileText}
                onClick={() => setFormat("PDF")}
              />

              <FormatCard
                active={format === "Excel"}
                title="Excel"
                icon={FileSpreadsheet}
                onClick={() => setFormat("Excel")}
              />

              <FormatCard
                active={format === "CSV"}
                title="CSV"
                icon={FileArchive}
                onClick={() => setFormat("CSV")}
              />

            </div>

          </div>

          {/* Filters */}

          <div className="grid md:grid-cols-2 gap-6">

            <Input
              icon={Calendar}
              label="From Date"
              type="date"
              value={form.from}
              onChange={(v) => update("from", v)}
            />

            <Input
              icon={Calendar}
              label="To Date"
              type="date"
              value={form.to}
              onChange={(v) => update("to", v)}
            />

            <div>

              <label className="block mb-2 text-xs uppercase tracking-widest text-gray-400">
                Branch
              </label>

              <div className="relative">

                <Building2
                  size={18}
                  className="absolute left-3 top-3 text-gray-500"
                />

                <select
                  value={form.branch}
                  onChange={(e) =>
                    update("branch", e.target.value)
                  }
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-black/40 border border-white/10"
                >
                  <option>All Branches</option>
                  <option>Main Branch</option>
                  <option>Factory</option>
                  <option>City Outlet</option>
                </select>

              </div>

            </div>

            <div className="flex items-end">

              <label className="flex items-center gap-3 cursor-pointer">

                <input
                  type="checkbox"
                  checked={form.includeZero}
                  onChange={(e) =>
                    update(
                      "includeZero",
                      e.target.checked
                    )
                  }
                />

                <span className="text-sm">
                  Include Zero Balance Accounts
                </span>

              </label>

            </div>

          </div>

          {/* Preview */}

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">

            <h3 className="font-serif text-lg mb-4">
              Export Summary
            </h3>

            <div className="space-y-3 text-sm">

              <Row
                label="Format"
                value={format}
              />

              <Row
                label="Date Range"
                value={`${form.from} → ${form.to}`}
              />

              <Row
                label="Branch"
                value={form.branch}
              />

              <Row
                label="Zero Balance Accounts"
                value={
                  form.includeZero
                    ? "Included"
                    : "Excluded"
                }
              />

            </div>

          </div>

        </div>

        {/* Footer */}

        <div className="flex justify-end gap-4 px-8 py-5 border-t border-white/10">

          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-white/10 hover:bg-white/5"
          >
            Cancel
          </PermissionButton>

          <PermissionButton permissionModule="accounts"
            onClick={exportReport}
            className="flex items-center gap-2 px-6 py-2 rounded-xl bg-amber-400 text-black font-semibold"
          >
            <Download size={18} />
            Export {format}
          </PermissionButton>

        </div>

      </div>

    </div>
  );
}

/* ---------- Components ---------- */

function Input({
  icon: Icon,
  label,
  value,
  onChange,
  type = "text",
}) {
  return (
    <div>

      <label className="block mb-2 text-xs uppercase tracking-widest text-gray-400">
        {label}
      </label>

      <div className="relative">

        <Icon
          size={18}
          className="absolute left-3 top-3 text-gray-500"
        />

        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-black/40 border border-white/10"
        />

      </div>

    </div>
  );
}

function FormatCard({
  active,
  title,
  icon: Icon,
  onClick,
}) {
  return (
    <PermissionButton permissionModule="accounts"
      onClick={onClick}
      className={`rounded-xl border p-5 transition ${
        active
          ? "border-amber-400 bg-amber-400/10"
          : "border-white/10 hover:border-amber-400/30 bg-white/5"
      }`}
    >
      <Icon
        size={32}
        className={`mx-auto ${
          active
            ? "text-amber-400"
            : "text-gray-400"
        }`}
      />

      <p className="mt-3 font-medium">
        {title}
      </p>
    </PermissionButton>
  );
}

function Row({
  label,
  value,
}) {
  return (
    <div className="flex justify-between items-center border-b border-white/10 pb-3">

      <span className="text-gray-400">
        {label}
      </span>

      <span className="font-medium">
        {value}
      </span>

    </div>
  );
}
